import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { deviceGroupsApi } from "../../lib/api/network/deviceGroups";
import { t } from "../../lib/i18n";
import { SCOPE_PRESETS, presetRange, useInsightsScope } from "../../lib/useInsightsScope";
import { Select } from "../ui/Select";

/**
 * Workspace-level scope filter for Insights. Lives at the top of
 * ``InsightsWorkspace`` and writes ``?from=&to=&device_group=&platform=`` to
 * the URL — every tab reads the same scope via ``useInsightsScope()``.
 *
 * Three controls:
 * - Date range preset (24h / 7d / 30d / 90d), with an explicit "custom" state
 *   when ``from``/``to`` are set to non-preset values
 * - Device group (single-select; defaults to "all")
 * - Platform (android / ios / all)
 */
export function ScopeFilter() {
  const [params, setParams] = useSearchParams();
  const scope = useInsightsScope();

  const groups = useQuery({
    queryKey: ["deviceGroups", "scope-filter"],
    queryFn: () => deviceGroupsApi.list(),
    staleTime: 5 * 60 * 1000,
  });

  const update = (next: Partial<Record<string, string | undefined>>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") {
        merged.delete(key);
      } else {
        merged.set(key, value);
      }
    }
    setParams(merged, { replace: false });
  };

  const activePreset =
    SCOPE_PRESETS.find((p) => {
      const r = presetRange(p.days);
      return r.from === scope.from && r.to === scope.to;
    })?.key ?? (scope.from || scope.to ? "custom" : "");

  return (
    <div
      className="filter-bar"
      role="region"
      aria-label={t("insights.scope.ariaLabel")}
      style={{ marginBottom: 16 }}
    >
      <Select
        value={activePreset}
        onChange={(e) => {
          const key = e.target.value;
          if (!key) {
            update({ from: undefined, to: undefined });
            return;
          }
          if (key === "custom") return;
          const preset = SCOPE_PRESETS.find((p) => p.key === key);
          if (!preset) return;
          const range = presetRange(preset.days);
          update({ from: range.from, to: range.to });
        }}
        aria-label={t("insights.scope.dateRange")}
      >
        <option value="">{t("insights.scope.dateRange.all")}</option>
        {SCOPE_PRESETS.map((p) => (
          <option key={p.key} value={p.key}>
            {t(`insights.scope.preset.${p.key}`)}
          </option>
        ))}
        {activePreset === "custom" && (
          <option value="custom">{t("insights.scope.preset.custom")}</option>
        )}
      </Select>

      <Select
        value={scope.device_group ?? ""}
        onChange={(e) => update({ device_group: e.target.value || undefined })}
        aria-label={t("insights.scope.deviceGroup")}
      >
        <option value="">{t("insights.scope.deviceGroup.all")}</option>
        {(groups.data ?? []).map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </Select>

      <Select
        value={scope.platform ?? ""}
        onChange={(e) => update({ platform: e.target.value || undefined })}
        aria-label={t("insights.scope.platform")}
      >
        <option value="">{t("insights.scope.platform.all")}</option>
        <option value="android">{t("common.platform.android")}</option>
        <option value="ios">{t("common.platform.ios")}</option>
      </Select>
    </div>
  );
}
